import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { IList, tuple } from 'annotation-tools/utils';
import React, { FC, memo, MouseEvent, useMemo } from 'react';
import styled from 'styled-components';
import { colorDef } from '../../../constants/styles/color-def';
import { viewTexts } from '../../../constants/view-texts';
import { IUserType } from '../../../types/record/user';
import { BreadcrumbsJp } from '../../breadcrumbs/breadcrumbs';
import { CreateNewUserDialog } from '../../dialogs/create-new-user-dialog/create-new-user-dialog';
import { DeleteUserDialog } from '../../dialogs/delete-user-dialog/delete-user-dialog';
import { EditUserDialog } from '../../dialogs/edit-user-dialog/edit-user-dialog';
import { MuiButton } from '../../shared/form-element';
import { SearchBar } from '../../shared/form-element/mui-search-bar';
import { MuiPaper } from '../../shared/mui/mui-paper';
import { MuiTablePagination } from '../../shared/mui/mui-table-pagination';
import { MuiTableRow } from '../../shared/mui/mui-table-row';
import { H2 } from '../../shared/styled/heading';
import { Root } from '../../shared/styled/root';

const vt = viewTexts.pages.userList;

const CreateButtonWrapper = styled.div`
  padding: 10px;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ButtonWrapper = styled.div`
  padding: 0 5px;
  display: inline-block;
`;

interface Props {
  searchString: string;
  onSearchStringChange: (value: string) => void;
  page: number;
  onPageChange: (value: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (value: number) => void;
  numRows: number;
  userListFiltered: IList<IUserType>;
  createNewUserOnClick: () => void;
  createNewUserDialogOpen: boolean;
  createNewUserDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  editUserOnClick: (user: IUserType) => void;
  editUserDialogIsOpen: boolean;
  editUserDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  userToEdit: IUserType | undefined;
  deleteUserOnClick: (user: IUserType) => void;
  deleteUserDialogIsOpen: boolean;
  deleteUserDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  userToDelete: IUserType | undefined;
}

export const UserListView: FC<Props> = memo(
  ({
    searchString,
    onSearchStringChange,
    page,
    onPageChange,
    rowsPerPage,
    onRowsPerPageChange,
    numRows,
    userListFiltered,
    createNewUserOnClick,
    createNewUserDialogOpen,
    createNewUserDialogOnClose,
    editUserOnClick,
    editUserDialogIsOpen,
    editUserDialogOnClose,
    userToEdit,
    deleteUserOnClick,
    deleteUserDialogIsOpen,
    deleteUserDialogOnClose,
    userToDelete
  }: Readonly<Props>) => {
    const usersWithHandler = useMemo(
      () =>
        userListFiltered.map(user =>
          tuple(
            {
              id: user.id,
              name: user.name,
              email: user.email,
              dateCreatedStr: new Date(user.dateCreated).toLocaleString(),
              dateUpdatedStr: new Date(user.dateUpdated).toLocaleString(),
              canBeDeleted: user.canBeDeleted
            },
            (event: MouseEvent) => {
              event.stopPropagation();
              editUserOnClick(user);
            },
            (event: MouseEvent) => {
              event.stopPropagation();
              deleteUserOnClick(user);
            }
          )
        ),
      [userListFiltered, editUserOnClick, deleteUserOnClick]
    );

    return (
      <Root>
        <BreadcrumbsJp />

        <H2>{vt.title}</H2>
        <SearchBarWrapper>
          <SearchBar
            placeholder={vt.searchBarPlaceholder}
            value={searchString}
            onInput={onSearchStringChange}
          />
          <CreateButtonWrapper>
            <MuiButton
              label={vt.buttons.create}
              onClick={createNewUserOnClick}
            />
          </CreateButtonWrapper>
        </SearchBarWrapper>

        <MuiPaper bgColor={colorDef.paperLighter} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{vt.header.name}</TableCell>
                <TableCell>{vt.header.emailAddress}</TableCell>
                <TableCell>{vt.header.dateCreated}</TableCell>
                <TableCell>{vt.header.dateUpdated}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {usersWithHandler.map(
                ([
                  user,
                  editButtonOnClickHandler,
                  deleteButtonOnClickHandler
                ]) => (
                  <MuiTableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.dateCreatedStr}</TableCell>
                    <TableCell>{user.dateUpdatedStr}</TableCell>
                    <TableCell align='right'>
                      <ButtonWrapper>
                        <MuiButton
                          label={vt.buttons.edit}
                          onClick={editButtonOnClickHandler}
                        />
                      </ButtonWrapper>
                      <ButtonWrapper
                        title={
                          user.canBeDeleted
                            ? undefined
                            : viewTexts.undeletableReason.user
                        }
                      >
                        <MuiButton
                          color='secondary'
                          label={vt.buttons.delete}
                          onClick={deleteButtonOnClickHandler}
                          disabled={!user.canBeDeleted}
                        />
                      </ButtonWrapper>
                    </TableCell>
                  </MuiTableRow>
                )
              )}
            </TableBody>
          </Table>
          <MuiTablePagination
            numRows={numRows}
            page={page}
            onChangePage={onPageChange}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={onRowsPerPageChange}
          />
        </MuiPaper>
        <CreateNewUserDialog
          open={createNewUserDialogOpen}
          onClose={createNewUserDialogOnClose}
        />
        {userToEdit !== undefined ? (
          <EditUserDialog
            open={editUserDialogIsOpen}
            onClose={editUserDialogOnClose}
            user={userToEdit}
          />
        ) : (
          undefined
        )}
        {userToDelete !== undefined ? (
          <DeleteUserDialog
            open={deleteUserDialogIsOpen}
            onClose={deleteUserDialogOnClose}
            user={userToDelete}
          />
        ) : (
          undefined
        )}
      </Root>
    );
  }
);

UserListView.displayName = 'UserListView';
